import React, { useEffect, useMemo } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { timezonesOptions } from '../../../../lib/utils/timezones'
import { find } from 'lodash'
import TextInput from '../../../../components/FormElements/TextInput'
import SelectInput from '../../../../components/FormElements/SelectInput'
import { Button } from '@demandlocal/components'
import { useGetCustomerSourceByCustomerSourceId } from '../../../../api/dealership/customers/sources/useGetCustomerSourceByCustomerSourceId'
import { useModal } from '../../../../components/useModal'
import { useEditCustomerSourceByCustomerSourceId } from '../../../../api/dealership/customers/sources/useEditCustomerSourceByCustomerSourceId'
import toast from 'react-hot-toast'

const fetchFrequencyOptions = [
  { label: 'One time', value: 'one_time' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
]
const fetchDayOptions = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
]

type Props = {
  customer_source_id: number
}
const EditFTPSource = (props: Props): JSX.Element => {
  const { closeModal } = useModal()
  const { customer_source_id } = props
  const { data: source, isLoading } =
    useGetCustomerSourceByCustomerSourceId(customer_source_id)
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const parsedConfig = useMemo(() => JSON.parse(source?.config ?? '{}'), [source?.config])
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = formContext

  const formValues = watch()
  const { mutateAsync: editCustomerSource } = useEditCustomerSourceByCustomerSourceId()

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Saving...')
    await editCustomerSource({
      data: {
        customer_source_id,
        customer_source: values.customer_source,
        data_provider_id: 1,
        url: values.ftp_url,
        username: values.ftp_username,
        password: values.ftp_password,
        config: {
          ...parsedConfig,
          fetch_frequency: values?.fetch_frequency?.value,
          fetch_time: values?.fetch_time?.value,
          fetch_day: values?.fetch_day?.value,
          fetch_timezone: values?.timezone,
        },
      },
    })
    toast.success('Saved!', { id: toastId })
    closeModal()
  }

  const hoursOptions = useMemo(
    () =>
      Array(48)
        .fill(0)
        .map((_, index) =>
          dayjs()
            .set('h', Math.floor(0.5 * index))
            .set('m', 30 * (index % 2))
            .format('h:mm A'),
        )
        .map((hour) => ({
          label: hour,
          value: hour,
        })),
    [],
  )

  useEffect(() => {
    if (!isLoading) {
      reset({
        fetch_frequency: find(fetchFrequencyOptions, {
          value: parsedConfig?.fetch_frequency,
        }),
        fetch_day: find(fetchDayOptions, {
          value: parsedConfig?.fetch_day,
        }),
        fetch_time: find(hoursOptions, {
          value: parsedConfig?.fetch_time,
        }),
        timezone: find(timezonesOptions, {
          value: parsedConfig?.fetch_timezone?.value,
        }),
        ftp_url: source?.url,
        ftp_username: source?.username,
        ftp_password: source?.password,
        customer_source: source?.customer_source,
      })
    }
  }, [
    source?.customer_source,
    source?.url,
    hoursOptions,
    parsedConfig.fetch_day,
    parsedConfig.fetch_frequency,
    parsedConfig.fetch_time,
    parsedConfig.fetch_timezone,
    reset,
    source?.username,
    source?.password,
    isLoading,
  ])

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col px-12 overflow-auto'}>
          <div className={'font-bold text-blue-primary text-[28px]'}>Edit source</div>
          <div className={'pt-8 pb-8 border-b-2'}>
            <div className={'w-1/2'}>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                name={'customer_source'}
                label={'Name'}
                control={control}
              />
            </div>
          </div>
          <div className={'pt-8 pb-8'}>
            <div className={'w-1/2'}>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={'Ex: ftp://mydomain.com/products.csv'}
                name={'ftp_url'}
                label={'File URL'}
                control={control}
                info={
                  'A fully qualified URL to access your file. For example: ftp://mydomain.com/products.csv'
                }
              />
            </div>
          </div>
          <div className={'pb-8 border-b-2'}>
            <div className={'font-bold text-blue-primary text-sm'}>
              If your URL is pasword protected, please provide your login info
            </div>
            <div className={'w-1/2 pt-7'}>
              <TextInput
                inverted
                placeholder={'FTP Username (Optional)'}
                name={'ftp_username'}
                label={'Username'}
                control={control}
              />
            </div>
            <div className={'w-1/2 pt-7'}>
              <TextInput
                inverted
                placeholder={'FTP Password (Optional)'}
                name={'ftp_password'}
                label={'Password'}
                control={control}
              />
            </div>
          </div>
          <div className={'mt-7'}>
            <div className={'flex gap-5 mb-7'}>
              <div className={'w-full max-w-[50%]'}>
                <SelectInput
                  required={'This field is required'}
                  name={'fetch_frequency'}
                  label={'Fetch frequency'}
                  control={control}
                  options={fetchFrequencyOptions}
                />
              </div>
              {formValues.fetch_frequency?.value === 'weekly' && (
                <SelectInput
                  required={'This field is required'}
                  name={'fetch_day'}
                  label={'Fetch time'}
                  control={control}
                  options={fetchDayOptions}
                />
              )}
              {formValues.fetch_frequency &&
                formValues.fetch_frequency?.value !== 'one_time' && (
                  <SelectInput
                    required={'This field is required'}
                    disabled={
                      formValues.fetch_frequency?.value === 'one_time' ||
                      !formValues.fetch_frequency
                    }
                    name={'fetch_time'}
                    label={'Fetch time'}
                    control={control}
                    options={hoursOptions}
                  />
                )}
            </div>
            {formValues.fetch_frequency &&
              formValues.fetch_frequency?.value !== 'one_time' && (
                <SelectInput
                  required={'This field is required'}
                  disabled={
                    formValues.fetch_frequency?.value === 'one_time' ||
                    !formValues.fetch_frequency
                  }
                  name={'timezone'}
                  label={'Timezone'}
                  control={control}
                  options={timezonesOptions}
                />
              )}
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 px-12'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting} type={'submit'}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default EditFTPSource
